import { Row, Select, DatePicker, Modal, Radio, Button } from 'antd';
import BasicCard from '../../../components/basicCard/BasicCard';
import Icon from '@ant-design/icons';
import { FiSearch } from 'react-icons/fi';
import FormItem from 'antd/es/form/FormItem';
import { useEffect, useState } from 'react';
import { referenceDataSyncService } from '../../../services/referenceDataSyncService';
import dayjs from 'dayjs';
import StatCard from '../../../components/statCard/StatCard';
import ChartComponent, { CHART_TYPES } from '../../../components/chartComponent/ChartComponent';
import { BiCart, BiTrendingUp, BiTrendingDown } from 'react-icons/bi';
import fetcher from '../../../helpers/fetcher';
import { API_ENDPOINTS } from '../../../constants/apiEndpoints';
import { ENVIRONMENT_CONFIG } from '../../../constants/environmentConfig';
import { useIsOnlineContext } from '../../../contexts/IsOnlineContext';

type PriceLookupProps = {
  t: (key: string) => string;
};

interface PriceAnalysisData {
  averagePrice: number;
  maxPrice: number;
  minPrice: number;
  priceChange: number;
  priceChangePercentage: number;
  trendData: {
    categories: string[];
    series: Array<{
      name: string;
      data: number[];
      color?: string;
    }>;
  };
}

const PriceLookup = ({ t }: PriceLookupProps) => {
  const [products, setProducts] = useState<
    Array<{ refId: string; name: string; isActive: boolean }>
  >([]);
  const [selectedProduct, setSelectedProduct] = useState<string | null>(null);
  const [startDate, setStartDate] = useState<dayjs.Dayjs | null>(() => dayjs().subtract(1, 'year'));
  const [endDate, setEndDate] = useState<dayjs.Dayjs | null>(() => dayjs());
  const [timeInterval, setTimeInterval] = useState<'daily' | 'monthly'>('monthly');
  const [priceAnalysis, setPriceAnalysis] = useState<PriceAnalysisData | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
  const { isOnline } = useIsOnlineContext();

  useEffect(() => {
    const loadProducts = async () => {
      try {
        const productData = await referenceDataSyncService.getPresentations();
        setProducts(productData);
      } catch (error) {
        console.error('Failed to load products:', error);
      }
    };

    loadProducts();
  }, []);

  // Refetch data when time interval changes
  useEffect(() => {
    if (priceAnalysis && selectedProduct) {
      const searchParams = {
        productRefId: selectedProduct,
        startDate: startDate ? startDate.valueOf() : null,
        endDate: endDate ? endDate.valueOf() : null,
        timeInterval,
      };
      fetchPriceAnalysis(searchParams);
    }
  }, [timeInterval]);

  const fetchPriceAnalysis = async (searchParams: {
    productRefId: string | null;
    startDate: number | null;
    endDate: number | null;
    timeInterval: 'daily' | 'monthly';
  }) => {
    if (!searchParams.productRefId) return;

    // Only set loading to true if this is not a time interval change
    // This prevents the modal from blinking during time interval updates
    const isTimeIntervalUpdate = priceAnalysis !== null;
    if (!isTimeIntervalUpdate) {
      setLoading(true);
    }

    try {
      const queryParams: Record<string, string> = {};
      if (searchParams.startDate) queryParams.startTime = searchParams.startDate.toString();
      if (searchParams.endDate) queryParams.endTime = searchParams.endDate.toString();
      if (searchParams.productRefId) queryParams.productRefId = searchParams.productRefId;
      queryParams.timeInterval = searchParams.timeInterval;

      const response = await fetcher.get(
        API_ENDPOINTS.PRICE.GET_PUBLIC_PRICE_LOOKUP,
        queryParams,
        undefined,
        ENVIRONMENT_CONFIG.ANALYSIS_SERVICE
      );

      if (response.statusText === 'SUCCESS' && response.data?.success && response.data?.data) {
        const analytics = response.data.analytics;
        const trendData = response.data.trendData;

        setPriceAnalysis({
          averagePrice: analytics.averagePrice,
          maxPrice: analytics.maxPrice,
          minPrice: analytics.minPrice,
          priceChange: analytics.priceChange,
          priceChangePercentage: analytics.priceChangePercentage,
          trendData,
        });
      }
    } catch (error) {
      console.error('Failed to fetch price analysis:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    const searchParams = {
      productRefId: selectedProduct,
      startDate: startDate ? startDate.valueOf() : null,
      endDate: endDate ? endDate.valueOf() : null,
      timeInterval,
    };

    if (searchParams.productRefId) {
      fetchPriceAnalysis(searchParams);
      setIsModalVisible(true);
    }
  };

  return (
    <div className="price-lookup section mx-4 sm:mx-6 md:mx-8 lg:mx-10">
      <BasicCard>
        <div className="price-lookup-card p-3 sm:p-4 md:p-5">
          <Row className="title gap-1 text-lg font-semibold sm:text-xl md:text-2xl">
            <Icon className="text-primary">
              <FiSearch />
            </Icon>
            <h2>{t('priceLookup')}</h2>
          </Row>
          <Row className="mt-4 flex flex-col items-stretch gap-3 sm:mt-5 sm:flex-row sm:flex-wrap sm:items-end lg:flex-nowrap">
            <FormItem
              className="config w-full sm:w-[calc(50%-0.375rem)] lg:min-w-[180px] lg:flex-1"
              layout="vertical"
              label={
                <label className="text-xs font-medium sm:text-sm">{t('searchProductLabel')}</label>
              }
            >
              <Select
                placeholder={t('searchProductPlaceholder')}
                value={selectedProduct}
                onChange={setSelectedProduct}
                allowClear
                showSearch
                size="large"
                className="w-full"
                filterOption={(input, option) =>
                  (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                }
                options={products.map((product) => ({
                  label: product.name,
                  value: product.refId,
                }))}
              />
            </FormItem>
            <FormItem
              className="config w-full sm:w-[calc(50%-0.375rem)] lg:min-w-[180px] lg:flex-1"
              layout="vertical"
              label={<label className="text-xs font-medium sm:text-sm">{t('startDate')}</label>}
            >
              <DatePicker
                className="w-full"
                placeholder={t('startDatePlaceholder')}
                value={startDate}
                onChange={setStartDate}
                format="YYYY-MM-DD"
                size="large"
              />
            </FormItem>
            <FormItem
              className="config w-full sm:w-[calc(50%-0.375rem)] lg:min-w-[180px] lg:flex-1"
              layout="vertical"
              label={<label className="text-xs font-medium sm:text-sm">{t('endDate')}</label>}
            >
              <DatePicker
                className="w-full"
                placeholder={t('endDatePlaceholder')}
                value={endDate}
                onChange={setEndDate}
                format="YYYY-MM-DD"
                size="large"
              />
            </FormItem>

            <Button
              type="primary"
              size="large"
              className="h-auto w-full rounded-full px-6 py-2.5 sm:w-[calc(50%-0.375rem)] sm:px-8 sm:py-3 lg:mb-[1.5px] lg:w-auto lg:min-w-[140px] lg:flex-1 lg:self-end"
              onClick={handleSearch}
              disabled={!isOnline}
              icon={<FiSearch />}
            >
              <span className="text-sm sm:text-base">{t('priceLookupCTA')}</span>
            </Button>
          </Row>
        </div>
      </BasicCard>

      <Modal
        title={t('priceLookupResults')}
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        footer={null}
        width="95%"
        style={{ maxWidth: '1500px' }}
        destroyOnClose
        centered
      >
        {priceAnalysis ? (
          <div className="space-y-4 sm:space-y-6">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4 lg:grid-cols-4">
              <StatCard
                value={priceAnalysis.averagePrice}
                icon={<BiCart />}
                title={t('averagePriceLabel')}
                statColor="#00b050"
              />
              <StatCard
                value={priceAnalysis.maxPrice}
                icon={<BiTrendingUp />}
                title={t('maximumPrice')}
                statColor="#ff0000"
              />
              <StatCard
                value={priceAnalysis.minPrice}
                icon={<BiTrendingDown />}
                title={t('minimumPrice')}
                statColor="#0070c0"
              />
              <StatCard
                value={priceAnalysis.priceChangePercentage}
                icon={priceAnalysis.priceChange >= 0 ? <BiTrendingUp /> : <BiTrendingDown />}
                title={priceAnalysis.priceChange >= 0 ? t('priceIncrease') : t('priceDecrease')}
                postfix="%"
                statColor={priceAnalysis.priceChange >= 0 ? '#ff0000' : '#0070c0'}
              />
            </div>

            <div>
              <div className="mb-3 flex flex-col gap-3 sm:mb-4 sm:flex-row sm:items-center sm:justify-between">
                <h3 className="text-base font-semibold sm:text-lg">{t('priceTrendsOverTime')}</h3>
                <div className="flex items-center gap-2 sm:gap-3">
                  {loading && priceAnalysis && (
                    <div className="flex items-center gap-2 text-xs text-gray-500 sm:text-sm">
                      <div className="h-3 w-3 animate-spin rounded-full border-b-2 border-primary sm:h-4 sm:w-4"></div>
                      <span className="hidden sm:inline">{t('updating')}</span>
                    </div>
                  )}
                  <Radio.Group
                    value={timeInterval}
                    onChange={(e) => setTimeInterval(e.target.value as 'daily' | 'monthly')}
                    size="small"
                    className="text-xs sm:text-sm"
                  >
                    <Radio.Button value="daily">{t('daily')}</Radio.Button>
                    <Radio.Button value="monthly">{t('monthly')}</Radio.Button>
                  </Radio.Group>
                </div>
              </div>
              <ChartComponent
                title=""
                type={CHART_TYPES.LINE}
                series={priceAnalysis.trendData.series}
                options={{
                  chart: {
                    toolbar: { show: false },
                  },
                  stroke: {
                    curve: 'monotoneCubic',
                    width: 2,
                  },
                  markers: {
                    size: 4,
                  },
                  dataLabels: {
                    enabled: false,
                  },
                  xaxis: {
                    categories: priceAnalysis.trendData.categories,
                    title: {
                      text: timeInterval === 'daily' ? t('date') : t('month'),
                    },
                    labels: {
                      style: {
                        fontSize: '10px',
                      },
                      rotate: -45,
                      rotateAlways: true,
                    },
                  },
                  colors: ['#00b050', '#ff0000', '#0070c0'],
                  legend: {
                    position: 'bottom',
                    fontSize: '12px',
                  },
                  grid: {
                    borderColor: '#e7e7e7',
                    strokeDashArray: 4,
                  },
                  yaxis: {
                    title: {
                      text: t('price'),
                    },
                    labels: {
                      style: {
                        fontSize: '10px',
                      },
                    },
                  },
                  responsive: [
                    {
                      breakpoint: 640,
                      options: {
                        chart: {
                          height: 300,
                        },
                        xaxis: {
                          labels: {
                            style: {
                              fontSize: '8px',
                            },
                          },
                        },
                        yaxis: {
                          labels: {
                            style: {
                              fontSize: '8px',
                            },
                          },
                        },
                        legend: {
                          fontSize: '10px',
                        },
                      },
                    },
                  ],
                }}
                maxHeight={400}
                loading={loading}
              />
            </div>
          </div>
        ) : (
          <div className="py-6 text-center sm:py-8">
            <p className="text-sm text-gray-500 sm:text-base">{t('noPriceData')}</p>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default PriceLookup;
